import { KLineData } from 'klinecharts';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { MessageBusService } from '../message-bus.service';
import { Logger } from '../logging/logger.interface';
import { LoggerService } from '../logging/logger-factory.service';
import { PriceDataFeedBackendService } from './price-data-feed-backend-service.class';
import { PriceBarDto } from '../shell-communication/dtos/price-bar-dto.interface';
import { PriceBarMessageDto } from '../shell-communication/dtos/price-bar-message-dto.interface';
import { GetPriceBars } from '../shell-communication/operations/price-charts/get-price-bars.class';
import { SubscribePriceBars } from '../shell-communication/operations/price-charts/subscribe-price-bars.class';
import { UnsubscribePriceBars } from '../shell-communication/operations/price-charts/unsubscribe-price-bars.class';
import { ReloadPriceChartsUIMessage } from '../ui-messages/reload-price-charts-ui-message.interface';
import { toKLineData, normalizeKLineData, NormalizationStats } from './price-chart-data-adapter';

export interface PriceChartLiveBar {
   subscriptionKey: string;
   bar: KLineData;
}

interface SubscriberInfo {
   ticker: string;
   timeframe: string;
}

export class PriceChartDataFeed {

   constructor(
      private readonly _componentId: string,
      private readonly _backend: PriceDataFeedBackendService,
      private readonly _messageBus: MessageBusService,
      private readonly _toastr: ToastrService,
      loggerService: LoggerService
   ) {
      this._logger = loggerService.createLogger(`PriceChartDataFeed(${this._componentId})`);
   }

   readonly bars$ = new Subject<PriceChartLiveBar>();
   readonly reload$ = new Subject<void>();

   private readonly _subscribers: Record<string, SubscriberInfo> = {};
   private readonly _unsubscriber = new Subject<void>();
   private _logger: Logger;
   private _disposed = false;
   private _initialized = false;

   init(): void {
      if (this._initialized) {
         return;
      }
      this._initialized = true;

      this._messageBus.of<PriceBarMessageDto>('PriceBarMessageDto')
         .pipe(takeUntil(this._unsubscriber))
         .subscribe(msg => this._onPriceBar(msg.payload));

      this._messageBus.of<ReloadPriceChartsUIMessage>('ReloadPriceChartsUIMessage')
         .pipe(takeUntil(this._unsubscriber))
         .subscribe(() => this._onReload());
   }

   async loadHistory(ticker: string, timeframe: string, from: Date, to: Date): Promise<{ bars: KLineData[]; lastDto: PriceBarDto | null; stats: NormalizationStats }> {
      if (this._disposed) {
         return { bars: [], lastDto: null, stats: { rawCount: 0, duplicateTsCount: 0, zeroRangeBarCount: 0, zeroRangeBarDropped: 0, monotonicAscending: true } };
      }

      const qry = new GetPriceBars(ticker, timeframe, from, to);

      let dtos: PriceBarDto[];
      try {
         dtos = await this._backend.getPriceBars(qry);
      } catch (e) {
         this._logger.error(e);
         this._toastr.error('"Get Price Bars" operation completed with errors', 'Price Chart');
         throw e;
      }

      const rawBars = dtos.map(toKLineData);
      const { bars, stats } = normalizeKLineData(rawBars);
      const lastDto = dtos.length > 0 ? dtos[dtos.length - 1] : null;
      return { bars, lastDto, stats };
   }

   async subscribeBars(ticker: string, timeframe: string, baseBar: PriceBarDto): Promise<string> {
      if (this._disposed || !baseBar) {
         return null;
      }

      const subscriptionKey = this._makeSubscriptionKey(ticker, timeframe);

      const cmd = new SubscribePriceBars(ticker, timeframe, subscriptionKey, baseBar);

      try {
         await this._backend.subscribePriceBars(cmd);
         this._subscribers[subscriptionKey] = { ticker, timeframe };
         this._logger.debug(`Subscribed: ${subscriptionKey}`);
         return subscriptionKey;
      } catch (e) {
         this._logger.error(e);
         this._toastr.error('"Subscribe Price Bars" operation completed with errors', 'Price Chart');
         return null;
      }
   }

   async unsubscribeBars(subscriptionKey: string): Promise<void> {
      const sub = this._subscribers[subscriptionKey];
      if (!sub) {
         return;
      }
      delete this._subscribers[subscriptionKey];

      if (this._disposed) {
         return;
      }

      const cmd = new UnsubscribePriceBars(sub.ticker, sub.timeframe, subscriptionKey);
      try {
         await this._backend.unsubscribePriceBars(cmd);
         this._logger.debug(`Unsubscribed: ${subscriptionKey}`);
      } catch (e) {
         this._logger.error(e);
      }
   }

   dispose(): void {
      if (this._disposed) {
         return;
      }
      this._disposed = true;

      this._unsubscriber.next();
      this._unsubscriber.complete();

      Object.keys(this._subscribers).forEach(key => {
         const sub = this._subscribers[key];
         const cmd = new UnsubscribePriceBars(sub.ticker, sub.timeframe, key);
         this._backend.unsubscribePriceBars(cmd).catch(() => {});
         delete this._subscribers[key];
      });

      this.bars$.complete();
      this.reload$.complete();
   }

   private _onPriceBar(msg: PriceBarMessageDto): void {
      const sub = this._subscribers[msg.subscriptionKey];
      if (!sub) {
         return;
      }
      this.bars$.next({
         subscriptionKey: msg.subscriptionKey,
         bar: toKLineData(msg.priceBar)
      });
   }

   private _onReload(): void {
      this._logger.info('Received "ReloadPriceChartsUIMessage"');
      this.reload$.next();
   }

   private _makeSubscriptionKey(ticker: string, timeframe: string): string {
      return `pricechart_${this._componentId}_${ticker}_${timeframe}`;
   }
}
